import { ReactNode, useEffect, useMemo, useReducer, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';

// common
import { PanelDataContext, type TabsData, type PanelContextType } from 'modules/context';

// screen
import useSupplierGeneralDetails from 'modules/suppliers/services/useSupplierGeneralDetails';
import useBusinessRegistrationCertificate from 'modules/suppliers/services/useBusinessRegistrationCertificate';
import useSupplyContract from 'modules/suppliers/services/useSupplyContract';
import useFoodSafetyCertificate from 'modules/suppliers/services/useFoodSafetyCertificate';
import useOtherDocuments from 'modules/suppliers/services/useOtherDocuments';
import useQuickSearchSupplierDetail from 'modules/quick-search/services/useQuickSearchSupplierDetail';

type ReduceActionType =
  | 'SUPPLIER_DETAILS'
  | 'BUSINESS_REGISTRATION'
  | 'SUPPLY_CONTRACT'
  | 'FOOD_SAFETY'
  | 'OTHER_DOCUMENTS'
  | 'PARAMS'; // các thông tin liên quan trên URL

const tabsDataReducer = (
  state: TabsData,
  action: { type: ReduceActionType; payload: any }
): TabsData => {
  if (!action.payload) return { ...state };

  switch (action.type) {
    case 'SUPPLIER_DETAILS':
      return { ...state, generalDetails: action.payload };
    case 'BUSINESS_REGISTRATION':
      return { ...state, businessRegistrationCertificates: action.payload };
    case 'SUPPLY_CONTRACT':
      return { ...state, supplyContract: action.payload };
    case 'FOOD_SAFETY':
      return { ...state, certificateOfFoodSafety: action.payload };
    case 'OTHER_DOCUMENTS':
      return { ...state, otherDocuments: action.payload };
    case 'PARAMS':
      return { ...state, params: action.payload };
    default:
      return state;
  }
};

interface PanelDataProviderProps {
  children: ReactNode;
  viewOnly?: boolean;
}

export const PanelDataProvider: FC<PanelDataProviderProps> = ({ children }) => {
  const [tabsData, dispatch] = useReducer(tabsDataReducer, {
    generalDetails: null,
    goodsType: null,
    businessRegistrationCertificates: [],
    supplyContract: null,
    certificateOfFoodSafety: null,
    otherDocuments: null,
    params: {
      customerOperationCode: '',
      supplierOperationCode: '',
    },
  });

  // Hooks
  const navigate = useNavigate();
  const params = useParams<{
    customerOperationCode: string;
    selectedTab: string;
    supplierOperationCode: string;
    id: string; // Quick Search: chi tiết theo id NCC (toàn hệ thống, không cần khách hàng)
  }>();

  // Nhánh Quick Search: nạp chi tiết từ endpoint toàn hệ thống theo id.
  // (Các hook customer-scoped bên dưới tự tắt vì thiếu customerOperationCode/supplierOperationCode.)
  const { data: qsDetail } = useQuickSearchSupplierDetail(
    params.id ? Number(params.id) : undefined
  );

  const apiParams = useMemo(() => {
    return {
      customerOperationCode: params.customerOperationCode,
      supplierProfileOperationCode: params.supplierOperationCode,
    };
  }, [params]);

  // API hooks
  const { data: supplierDetails } = useSupplierGeneralDetails(apiParams);

  const { data: businessRegistration } = useBusinessRegistrationCertificate(apiParams);

  const { data: supplyContract } = useSupplyContract(apiParams);

  const { data: foodSafetyCertificate } = useFoodSafetyCertificate(apiParams);

  const { data: otherDocuments } = useOtherDocuments(apiParams);

  // Actions
  const toPreviousPage = () => {
    const suppliersList = '/suppliers';
    navigate(suppliersList);
  };

  useEffect(() => {
    if (!supplierDetails) return;

    if (supplierDetails.status !== 'available') {
      navigate('404');
    }

    dispatch({ type: 'SUPPLIER_DETAILS', payload: supplierDetails });
  }, [supplierDetails, navigate]);

  // Quick Search: supplier_profile.show trả đúng shape generalDetails → đổ thẳng vào cùng key.
  useEffect(() => {
    if (!qsDetail) return;
    dispatch({ type: 'SUPPLIER_DETAILS', payload: qsDetail });
  }, [qsDetail]);

  useEffect(() => {
    if (!businessRegistration) return;
    dispatch({ type: 'BUSINESS_REGISTRATION', payload: businessRegistration });
  }, [businessRegistration]);

  useEffect(() => {
    if (!supplyContract) return;
    dispatch({ type: 'SUPPLY_CONTRACT', payload: supplyContract });
  }, [supplyContract]);

  useEffect(() => {
    if (!foodSafetyCertificate) return;
    dispatch({ type: 'FOOD_SAFETY', payload: foodSafetyCertificate });
  }, [foodSafetyCertificate]);

  useEffect(() => {
    if (!otherDocuments) return;
    dispatch({ type: 'OTHER_DOCUMENTS', payload: otherDocuments });
  }, [otherDocuments]);

  useEffect(() => {
    dispatch({
      type: 'PARAMS',
      payload: { ...params },
    });
  }, [params]);

  const contextValue: PanelContextType = {
    ...tabsData,
    toPreviousPage,
  };

  return <PanelDataContext.Provider value={contextValue}>{children}</PanelDataContext.Provider>;
};
